import { Injectable, inject } from '@angular/core';
import { Logger } from '@nsalaun/ng-logger';
import * as Sentry from '@sentry/angular';
import { filter, take } from 'rxjs/operators';

import { EnrichedError } from '@svg-frontends/error/models/enriched-error.model';
import { ErrorHandlingService } from '@svg-frontends/error/services/error-handling.service';
import { ContextState } from '@svg-frontends/session/context/enums/context-state.enum';
import { AsiContext } from '@svg-frontends/session/context/models/asi-context.model';
import { SvgContext } from '@svg-frontends/session/context/models/svg-context.model';
import { AsiContextService } from '@svg-frontends/session/context/services/asi-context.service';
import { SvgContextService } from '@svg-frontends/session/context/services/svg-context.service';

import { ApplicationNamesEnum } from '@svg/environment/application-names.enum';
import { APPLICATION_NAME, APP_VERSION_TOKEN, STAGE_TOKEN } from '@svg/environment/injection-tokens';
import { StagesEnum } from '@svg/environment/stages.enum';

@Injectable({ providedIn: 'root' })
export class LoggingService {
	private applicationName = inject(APPLICATION_NAME);
	private appVersion = inject(APP_VERSION_TOKEN);
	private stage = inject<StagesEnum>(STAGE_TOKEN);
	private asiContextService = inject(AsiContextService);
	private svgContextService = inject(SvgContextService);
	private errorHandlerService = inject(ErrorHandlingService);
	private logger = inject(Logger);

	/**
	 * Sentry's captureException function only captures an Error
	 * to capture an HttpErrorResponse, use captureMessage (HttpErrorResponse has no stack)
	 */
	static sendViaSentry(enrichedError: EnrichedError): void {
		Sentry.setExtra('displayMessage', enrichedError?.displayMessages?.join(', ') || undefined);
		Sentry.captureException(enrichedError.error);
	}

	/**
	 * Track an application metric count e.g. for click tracks
	 * @param eventName
	 * @param count The value to increment by (defaults to 1).
	 * @param attributes Arbitrary structured data that stores information about the metric.
	 */
	static metricCount(eventName: string, count = 1, attributes?: Record<string, unknown>) {
		Sentry.metrics.count(eventName, count, { attributes });
	}

	init(): void {
		this.initContextChange();
		this.initErrorLogging();
	}

	private initContextChange(): void {
		if (this.applicationName === ApplicationNamesEnum.myASI) {
			this.asiContextService
				.getAsiContext()
				.pipe(
					// filter to catch cases on initial login where this is called before login
					// prevent initializing sentry with missing data
					filter((context) => context.state !== ContextState.NOT_LOGGED_IN),
					// take only first meaningful logged in context then kill subscription
					// sentry.configureScope() only calls parameter function once anyway
					take(1),
				)
				.subscribe((asiContext: AsiContext) => this.handleAsiContextChange(asiContext));
		} else {
			this.svgContextService
				.getSvgContext()
				.pipe(
					// see comment for myasi above
					filter((context) => context.state !== ContextState.NOT_LOGGED_IN),
					take(1),
				)
				.subscribe((svgContext: SvgContext) => this.handleSvgContextChange(svgContext));
		}
	}

	private handleAsiContextChange(asiContext: AsiContext): void {
		{
			const scope = Sentry.getCurrentScope();
			scope.setTags({
				build: this.appVersion.split('-')[1],
			});

			// user info and customer info must always be set - on logout for example it must be nulled
			scope.setUser({
				email: asiContext.email,
				family_name: asiContext.lastName,
				given_name: asiContext.firstName,
				name: asiContext.userName,
				preferred_username: asiContext.userName,
				sub: asiContext.id,
			});

			Sentry.setTags({
				name: asiContext.userName,
				family_name: asiContext.lastName,
				preferred_username: asiContext.userName,
			});

			Sentry.setContext('company', {
				id: asiContext.customerInfo?.id,
				keycloakPath: asiContext.customerInfo?.customerNumber,
				name: asiContext.customerInfo?.displayName,
				svgId: asiContext.customerInfo?.asiSvgInfoDto.svg,
			});
		}
	}

	private handleSvgContextChange(svgContext: SvgContext): void {
		{
			const scope = Sentry.getCurrentScope();
			scope.setTags({
				build: this.appVersion.split('-')[1],
			});

			// user info and customer info must always be set - on logout for example it must be nulled
			scope.setUser({
				email: svgContext.user?.email,
				family_name: svgContext.user?.family_name,
				given_name: svgContext.user?.given_name,
				name: svgContext.user?.name,
				preferred_username: svgContext.user?.preferred_username,
				sub: svgContext.user?.sub,
			});

			Sentry.setTags({
				name: svgContext.user?.name,
				family_name: svgContext.user?.family_name,
				preferred_username: svgContext.user?.preferred_username,
			});

			Sentry.setContext('company', {
				customerGUID: svgContext.customerInfo?.customerGUID,
				keycloakPath: svgContext.customerInfo?.customerNumber,
				name: svgContext.customerInfo?.companyName,
				svgId: svgContext.customerInfo?.svgId,
			});
		}
	}

	private initErrorLogging(): void {
		this.errorHandlerService
			.getErrorObservable()
			.pipe(filter((enrichedError: EnrichedError) => enrichedError.reportSentry))
			.subscribe((enrichedError: EnrichedError) => this.logError(enrichedError));
	}

	/**
	 * log error to console and to sentry
	 */
	private logError(enrichedError: EnrichedError): void {
		LoggingService.sendViaSentry(enrichedError);

		const displayMessages = enrichedError.displayMessages.join(' ');
		if ([StagesEnum.PRODUCTION, StagesEnum.STAGING].includes(this.stage)) {
			// only log display message on production and staging, in other stages log full error (including stack) to make debugging easier
			this.logger.error(displayMessages);
		} else {
			const message = `${displayMessages} (${enrichedError.error.message})`;
			this.logger.error(message);
			this.logger.error(enrichedError.error);
		}
	}
}
